import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api';

// Create axios instance
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor - add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor - handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 || error.response?.status === 403) {
      // Clear auth state and redirect to login
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  getMe: () => api.get('/auth/me'),
  changePassword: (currentPassword, newPassword) =>
    api.post('/auth/change-password', { currentPassword, newPassword }),
};

// Extensions API
export const extensionsAPI = {
  getAll: () => api.get('/extensions'),
  getById: (id) => api.get(`/extensions/${id}`),
  create: (data) => api.post('/extensions', data),
  update: (id, data) => api.put(`/extensions/${id}`, data),
  delete: (id) => api.delete(`/extensions/${id}`),
  resetPassword: (id, newPassword) =>
    api.post(`/extensions/${id}/reset-password`, { newPassword }),
};

// Ring Groups API
export const ringGroupsAPI = {
  getAll: () => api.get('/ring-groups'),
  getById: (id) => api.get(`/ring-groups/${id}`),
  create: (data) => api.post('/ring-groups', data),
  update: (id, data) => api.put(`/ring-groups/${id}`, data),
  delete: (id) => api.delete(`/ring-groups/${id}`),
};

// IVR API
export const ivrAPI = {
  getAll: () => api.get('/ivr'),
  getById: (id) => api.get(`/ivr/${id}`),
  create: (data) => api.post('/ivr', data),
  update: (id, data) => api.put(`/ivr/${id}`, data),
  delete: (id) => api.delete(`/ivr/${id}`),
};

// Voicemail API
export const voicemailAPI = {
  getAll: () => api.get('/voicemail'),
  getByExtension: (extensionId) => api.get(`/voicemail/${extensionId}`),
  update: (extensionId, data) => api.put(`/voicemail/${extensionId}`, data),
};

// Time Conditions API
export const timeConditionsAPI = {
  getAll: () => api.get('/time-conditions'),
  getById: (id) => api.get(`/time-conditions/${id}`),
  create: (data) => api.post('/time-conditions', data),
  update: (id, data) => api.put(`/time-conditions/${id}`, data),
  delete: (id) => api.delete(`/time-conditions/${id}`),
};

// Time Groups API
export const timeGroupsAPI = {
  getAll: () => api.get('/time-groups'),
  create: (data) => api.post('/time-groups', data),
  update: (id, data) => api.put(`/time-groups/${id}`, data),
  delete: (id) => api.delete(`/time-groups/${id}`),
};

// CDR (Call History) API
export const cdrAPI = {
  getAll: (params) => api.get('/cdr', { params }),
  getStats: (params) => api.get('/cdr/stats', { params }),
};

// Dashboard API
export const dashboardAPI = {
  getStats: () => api.get('/dashboard/stats'),
};

// Destinations API (for dropdowns)
export const destinationsAPI = {
  getAll: () => api.get('/destinations'),
};

// Queues API
export const queuesAPI = {
  getAll: () => api.get('/queues'),
  getById: (id) => api.get(`/queues/${id}`),
  create: (data) => api.post('/queues', data),
  update: (id, data) => api.put(`/queues/${id}`, data),
  delete: (id) => api.delete(`/queues/${id}`),
};

// Admin API
export const adminAPI = {
  getUsers: () => api.get('/admin/users'),
  createUser: (data) => api.post('/admin/users', data),
  deactivateUser: (userId) => api.put(`/admin/users/${userId}/deactivate`),
};

export default api;
